/**
 * CreateCampaignModal Component
 *
 * Modal for creating new campaigns
 */

import { useState } from "react";
import { FaFolderOpen, FaTimes, FaSpinner } from "react-icons/fa";
import { useCampaignsData } from "../../hooks";
import "./CreateCampaignModal.scss";

export const CreateCampaignModal = ({ isOpen, onClose, onSuccess }) => {
  const { createCampaign } = useCampaignsData();
  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      return;
    }

    setLoading(true);
    try {
      const campaignId = await createCampaign(
        formData.name,
        formData.description
      );

      if (campaignId) {
        // Reset form
        setFormData({
          name: "",
          description: "",
        });

        // Call success callback
        if (onSuccess) {
          onSuccess(campaignId);
        }

        // Close modal
        onClose();
      }
    } catch (error) {
      console.error("Error creating campaign:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        name: "",
        description: "",
      });
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="create-campaign-modal-overlay" onClick={handleClose}>
      <div
        className="create-campaign-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit}>
          <div className="create-campaign-modal__header">
            <div className="create-campaign-modal__title">
              <FaFolderOpen />
              Create New Campaign
            </div>
            <button
              type="button"
              className="create-campaign-modal__close"
              onClick={handleClose}
              disabled={loading}
            >
              <FaTimes />
            </button>
          </div>

          <div className="create-campaign-modal__body">
            <p className="subtitle">
              Campaigns group related mailshots together and track which
              properties have been sent. Each property can only be sent once per
              campaign.
            </p>

            <div className="form-group">
              <label htmlFor="name">
                Campaign Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Spring 2025 Outreach"
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description (Optional)</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="e.g., Three-wave follow-up campaign for new Spring listings"
                rows="3"
                disabled={loading}
                style={{
                  width: "100%",
                  padding: "0.625rem 0.875rem",
                  border: "1px solid #d1d5db",
                  borderRadius: "0.375rem",
                  fontSize: "0.9375rem",
                  color: "#111827",
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  resize: "vertical",
                }}
              />
              <span className="help-text">
                Describe the purpose or strategy of this campaign
              </span>
            </div>
          </div>

          <div className="create-campaign-modal__footer">
            <button
              type="button"
              className="btn btn--secondary"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={loading || !formData.name.trim()}
            >
              {loading ? (
                <>
                  <FaSpinner className="spinner" />
                  Creating...
                </>
              ) : (
                "Create Campaign"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
